/**
 @license
 Copyright (c) 2015-2026 Lablup Inc. All rights reserved.
 */
import { VFolderTableProjectQuery } from '../__generated__/VFolderTableProjectQuery.graphql';
import { useBaiSignedRequestWithPromise } from '../helper';
import { useSuspendedBackendaiClient } from '../hooks';
import { useKeyPairLazyLoadQuery } from '../hooks/hooksUsingRelay';
import { useSuspenseTanQuery } from '../hooks/reactQueryAlias';
import useControllableState_deprecated from '../hooks/useControllableState';
import { useCurrentProjectValue } from '../hooks/useCurrentProject';
import FolderCreateModalV2 from './FolderCreateModalV2';
import { useFolderExplorerOpener } from './FolderExplorerOpener';
import TextHighlighter from './TextHighlighter';
import VFolderPermissionTag from './VFolderPermissionTag';
import { VFolder } from './VFolderSelect';
import {
  QuestionCircleOutlined,
  ReloadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Button,
  Descriptions,
  Form,
  Input,
  Space,
  TableProps,
  Tag,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import {
  BAIButton,
  BAIUserUnionIcon,
  BAIFlex,
  BAILink,
  BAITable,
  useEventNotStable,
  useUpdatableState,
} from 'backend.ai-ui';
import dayjs from 'dayjs';
import * as _ from 'lodash-es';
import { PlusIcon } from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { graphql, useLazyLoadQuery } from 'react-relay';

export interface VFolderFile {
  name: string;
  type: 'FILE' | 'DIRECTORY';
  size: number;
  mode: string;
  created: string;
  modified: string;
}
type VFolderKey = string;

export interface VFolderSelectValue {
  alias?: string;
  key: string;
}

export interface AliasMap {
  [key: string]: string;
}

type DataIndex = keyof VFolder;

export interface VFolderTableProps extends Omit<TableProps<VFolder>, 'rowKey'> {
  showAliasInput?: boolean;
  selectedRowKeys?: VFolderKey[];
  onChangeSelectedRowKeys?: (
    selectedKeys: VFolderKey[],
    selectedVFolders: VFolder[],
  ) => void;
  aliasBasePath?: string;
  aliasMap?: AliasMap;
  onChangeAliasMap?: (aliasMap: AliasMap) => void;
  rowFilter?: (vFolder: VFolder) => boolean;
  rowKey: string | number;
  onChangeAutoMountedFolders?: (names: Array<string>) => void;
  showAutoMountedFoldersSection?: boolean;
  ownerEmail?: string;
  onValidateSelectedRowKeys?: (
    invalidKeys: VFolderKey[],
    validVFolders: VFolder[],
  ) => void;
}

export const vFolderAliasNameRegExp = /^[a-zA-Z0-9_/.-]*$/;
export const DEFAULT_ALIAS_BASE_PATH = '/home/work/';
const VFolderTable: React.FC<VFolderTableProps> = ({
  rowFilter,
  showAliasInput = false,
  selectedRowKeys: controlledSelectedRowKeys = [],
  onChangeSelectedRowKeys,
  aliasBasePath = DEFAULT_ALIAS_BASE_PATH,
  aliasMap: controlledAliasMap,
  onChangeAliasMap,
  rowKey = 'name',
  onChangeAutoMountedFolders,
  showAutoMountedFoldersSection,
  ownerEmail,
  onValidateSelectedRowKeys,
  ...tableProps
}) => {
  'use memo';
  const { generateFolderPath } = useFolderExplorerOpener();
  const getRowKey = React.useMemo(() => {
    return (record: VFolder) => {
      const key = record && record[rowKey as DataIndex];
      return key as VFolderKey;
    };
  }, [rowKey]);

  const [isOpenCreateModal, setIsOpenCreateModal] = useState(false);

  const [selectedRowKeys, setSelectedRowKeys] = useControllableState_deprecated<
    VFolderKey[]
  >(
    {
      value: controlledSelectedRowKeys,
      onChange: (selectedKeys: VFolderKey[]) => {
        const selectedVFolders = _.filter(displayingFolders, (folder) =>
          _.includes(selectedKeys, getRowKey(folder)),
        );
        onChangeSelectedRowKeys?.(selectedKeys, selectedVFolders);
      },
    },
    {
      defaultValue: [],
    },
  );

  const [aliasMap, setAliasMap] = useControllableState_deprecated<AliasMap>(
    {
      value: controlledAliasMap,
      onChange: onChangeAliasMap,
    },
    {
      defaultValue: {},
    },
  );

  const baiClient = useSuspendedBackendaiClient();
  const [keypair] = useKeyPairLazyLoadQuery(baiClient?._config.accessKey);

  const [internalForm] = Form.useForm<AliasMap>();
  useEffect(() => {
    // TODO: check setFieldsValue performance
    if (aliasMap) {
      internalForm.setFieldsValue(
        _.mapValues(aliasMap, (v) => {
          if (v.startsWith(aliasBasePath)) {
            return v.slice(aliasBasePath.length);
          }
          return v;
        }),
      );
      internalForm.validateFields();
    }
  }, [aliasMap, internalForm, aliasBasePath]);

  const { t } = useTranslation();
  const { token } = theme.useToken();
  const baiRequestWithPromise = useBaiSignedRequestWithPromise();
  const currentProject = useCurrentProjectValue();

  if (!currentProject.id) {
    throw new Error('Project is required for VFolderTable');
  }

  const [fetchKey, updateFetchKey] = useUpdatableState('first');
  const [isPendingRefetch, startRefetchTransition] = useTransition();
  const { data: allFolderList } = useSuspenseTanQuery({
    queryKey: ['VFolderSelectQuery', fetchKey, currentProject.id, ownerEmail],
    queryFn: () => {
      const search = new URLSearchParams();
      // FIXME: filter by group_id does not work
      // search.set('group_id', currentProject.id);
      ownerEmail && search.set('owner_user_email', ownerEmail);
      return baiRequestWithPromise({
        method: 'GET',
        url: `/folders?${search.toString()}`,
      }) as Promise<VFolder[]>;
    },
    staleTime: 1000,
  });

  const { domain, group, keypair_resource_policy } =
    useLazyLoadQuery<VFolderTableProjectQuery>(
      graphql`
        query VFolderTableProjectQuery(
          $domain_name: String!
          $group_id: UUID!
          $keypair_resource_policy_name: String!
        ) {
          domain(name: $domain_name) {
            allowed_vfolder_hosts
          }
          group(id: $group_id, domain_name: $domain_name) {
            allowed_vfolder_hosts
          }
          keypair_resource_policy(name: $keypair_resource_policy_name) {
            allowed_vfolder_hosts
          }
        }
      `,
      {
        domain_name: baiClient._config.domainName,
        group_id: currentProject.id,
        keypair_resource_policy_name: keypair?.resource_policy || '',
      },
      {
        fetchPolicy: 'store-and-network',
        fetchKey: fetchKey,
      },
    );

  const mountableVolumesByPermission = useMemo(() => {
    const allowedVFolderHostsByDomain = JSON.parse(
      domain?.allowed_vfolder_hosts || '{}',
    );
    const allowedVFolderHostsByGroup = JSON.parse(
      group?.allowed_vfolder_hosts || '{}',
    );
    const allowedVFolderHostsByKeypairResourcePolicy = JSON.parse(
      keypair_resource_policy?.allowed_vfolder_hosts || '{}',
    );

    const mergedVFolderPermissions = _.merge(
      {}, // start with empty object
      allowedVFolderHostsByDomain,
      allowedVFolderHostsByGroup,
      allowedVFolderHostsByKeypairResourcePolicy,
    );
    // only allow mount if volume permission has 'mount-in-session'
    return Object.keys(mergedVFolderPermissions).filter((volume) =>
      mergedVFolderPermissions[volume].includes('mount-in-session'),
    );
  }, [domain, group, keypair_resource_policy]);

  const accessibleFoldersByCurrentProject = useMemo(() => {
    return (
      allFolderList?.filter(
        (folder) =>
          folder.ownership_type === 'user' ||
          !folder.group ||
          folder.group === currentProject.id,
      ) || []
    );
  }, [allFolderList, currentProject.id]);

  const mountableFoldersByPermission = useMemo(() => {
    return accessibleFoldersByCurrentProject.filter((folder) =>
      mountableVolumesByPermission.includes(folder.host),
    );
  }, [accessibleFoldersByCurrentProject, mountableVolumesByPermission]);

  useEffect(() => {
    // check selectedRowKeys are valid
    const invalidKeys = _.difference(
      selectedRowKeys,
      mountableFoldersByPermission.map((vf) => getRowKey(vf)),
    );

    onValidateSelectedRowKeys?.(
      invalidKeys,
      _.filter(mountableFoldersByPermission, (vf) =>
        _.includes(selectedRowKeys, getRowKey(vf)),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mountableFoldersByPermission,
    getRowKey,
    onValidateSelectedRowKeys,
    // Use JSON.stringify to compare array contents rather than reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(selectedRowKeys),
  ]);

  const autoMountedFolderNames = useMemo(
    () =>
      _.map(
        _.filter(
          mountableFoldersByPermission,
          (vf) => vf.status === 'ready' && vf.name?.startsWith('.'),
        ),
        (vf) => vf.name,
      ),
    [mountableFoldersByPermission],
  );

  useEffect(() => {
    _.isFunction(onChangeAutoMountedFolders) &&
      onChangeAutoMountedFolders(autoMountedFolderNames);
    // Omit `onChangeAutoMountedFolders` from deps so a parent re-render doesn't retrigger this effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoMountedFolderNames]);

  useEffect(() => {
    // Only reset selectedRowKeys when currentProject changes if there are no controlled selectedRowKeys
    if (!controlledSelectedRowKeys || controlledSelectedRowKeys.length === 0) {
      setSelectedRowKeys([]);
    }
    // Reset selectedRowKeys when currentProject changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject.id]);

  const [searchKey, setSearchKey] = useState('');
  const displayingFolders = _.filter(mountableFoldersByPermission, (vf) => {
    // Apply external filter for display
    if (rowFilter && !rowFilter(vf)) {
      return false;
    }
    // Always show selected items
    if (selectedRowKeys.includes(getRowKey(vf))) {
      return true;
    }
    // Apply search filter
    return !searchKey || vf.name.includes(searchKey);
  });

  /**
   * Converts the input path to an aliased path based on the provided name and input.
   * If the input is empty, it appends the name to the alias base path.
   * If the input starts with '/', it returns the input as is.
   * Otherwise, it appends the input to the alias base path.
   *
   * @param name - The name of the VFolderKey.
   * @param input - The input path to be converted.
   * @returns The aliased path based on the name and input.
   */
  const inputToAliasPath = useCallback(
    (name: VFolderKey, input?: string) => {
      if (input === undefined || input === '') {
        return `${aliasBasePath}${name}`;
      } else if (input.startsWith('/')) {
        return input;
      } else {
        return `${aliasBasePath}${input}`;
      }
    },
    [aliasBasePath],
  );

  const handleAliasUpdate = useEventNotStable(() => {
    // Only carry aliases for currently-selected rows. The internal form
    // intentionally preserves values for deselected rows (so re-selecting
    // restores the user's previous alias), but `aliasMap` is the
    // authoritative payload for the parent form's mount list and must
    // mirror the selection.
    const allValues = internalForm.getFieldsValue({ strict: false });
    setAliasMap(
      _.mapValues(
        _.pickBy(_.pick(allValues, selectedRowKeys), (v) => !!v),
        (v, k) => inputToAliasPath(k, v),
      ),
    );
    internalForm.validateFields().catch(() => {});
  });

  useEffect(() => {
    handleAliasUpdate();
    // `selectedRowKeys` can be changed by parents at any time, so we need to check whether `selectedRowKeys` has changed using JSON.stringify
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedRowKeys), handleAliasUpdate]);

  const columns: ColumnsType<VFolder> = [
    {
      title: (
        <BAIFlex direction="row" gap="xxs">
          <Typography.Text>{t('data.folders.Name')}</Typography.Text>
          {showAliasInput && (
            <>
              <Typography.Text
                type="secondary"
                style={{ fontWeight: 'normal' }}
              >
                ({t('session.launcher.FolderAlias')}{' '}
                <Tooltip
                  title={<Trans i18nKey={'session.launcher.DescFolderAlias'} />}
                >
                  <QuestionCircleOutlined />
                </Tooltip>
                )
              </Typography.Text>
            </>
          )}
        </BAIFlex>
      ),
      dataIndex: 'name',
      ellipsis: true,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (value, record) => {
        const isCurrentRowSelected = selectedRowKeys.includes(
          getRowKey(record),
        );

        return (
          <BAIFlex
            direction="column"
            align="start"
            gap={'xxs'}
            style={
              showAliasInput && isCurrentRowSelected
                ? { display: 'inline-flex', height: 70, width: '100%' }
                : {
                    overflow: 'hidden',
                    width: '100%',
                  }
            }
          >
            <BAILink
              type="hover"
              to={generateFolderPath(record.id)}
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
                display: 'block',
              }}
            >
              <TextHighlighter keyword={searchKey}>{value}</TextHighlighter>
            </BAILink>
            {showAliasInput && isCurrentRowSelected && (
              <Form.Item
                noStyle
                // rerender when
                shouldUpdate={(prev, cur) =>
                  prev[getRowKey(record)] !== cur[getRowKey(record)]
                }
              >
                {() => {
                  const allAliasPathMap = _.reduce(
                    selectedRowKeys,
                    (result: AliasMap, name) => {
                      result[name] =
                        aliasMap?.[name] || inputToAliasPath(name, undefined);

                      return result;
                    },
                    {} as AliasMap,
                  );

                  return (
                    <Form.Item
                      name={getRowKey(record)}
                      rules={[
                        {
                          // required: true,
                          type: 'string',
                          pattern: vFolderAliasNameRegExp,
                          message: t('session.launcher.FolderAliasInvalid'),
                        },
                        {
                          type: 'string',
                          validator: async (_rule, value) => {
                            if (
                              value &&
                              _.some(
                                allAliasPathMap,
                                (path, k) =>
                                  k !== getRowKey(record) && // not current row
                                  path ===
                                    inputToAliasPath(getRowKey(record), value),
                              )
                            ) {
                              return Promise.reject(
                                t('session.launcher.FolderAliasOverlapping'),
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                        {
                          type: 'string',
                          validator: async (_rule, value) => {
                            const aliasPath = inputToAliasPath(
                              getRowKey(record),
                              value,
                            );
                            if (
                              value &&
                              _.map(
                                autoMountedFolderNames,
                                // `n` is the name of the auto mounted folder. It cannot be empty.
                                (n) => inputToAliasPath('', n),
                              ).includes(aliasPath)
                            ) {
                              return Promise.reject(
                                t(
                                  'session.launcher.FolderAliasOverlappingToAutoMount',
                                ),
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                      // dependencies={[getRowKey(record)]}
                      extra={inputToAliasPath(
                        record.name,
                        internalForm.getFieldValue(getRowKey(record)),
                      )}
                    >
                      <Input
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        placeholder={t('session.launcher.FolderAlias')}
                        allowClear
                        onChange={() => {
                          handleAliasUpdate();
                        }}
                      ></Input>
                    </Form.Item>
                  );
                }}
              </Form.Item>
            )}
          </BAIFlex>
        );
      },
      // ...getColumnSearchProps('name'),
    },
    {
      title: t('data.UsageMode'),
      dataIndex: 'usage_mode',
      sorter: (a, b) => a.usage_mode.localeCompare(b.usage_mode),
    },
    {
      title: t('data.Host'),
      dataIndex: 'host',
    },
    {
      title: t('data.Type'),
      dataIndex: 'type',
      sorter: (a, b) => a.type.localeCompare(b.type),
      render: (_, record) => {
        return (
          <BAIFlex direction="column">
            {record.ownership_type === 'user' ? (
              <BAIFlex gap={'xs'}>
                <Typography.Text>{t('data.User')}</Typography.Text>
                <UserOutlined style={{ color: token.colorTextTertiary }} />
              </BAIFlex>
            ) : (
              <BAIFlex gap={'xs'}>
                <Typography.Text>{t('data.Project')}</Typography.Text>
                <BAIUserUnionIcon style={{ color: token.colorTextTertiary }} />
              </BAIFlex>
            )}
          </BAIFlex>
        );
      },
      // render: (value) =>
      //   value === 'group' ? (
      //     <GroupOutlined />
      //   ) : value === 'user' ? (
      //     <UserOutlined />
      //   ) : value ? (
      //     value
      //   ) : (
      //     '-'
      //   ),
      // filters: [
      //   {
      //     text: 'user',
      //     value: 'user',
      //   },
      //   {
      //     text: 'group',
      //     value: 'group',
      //   },
      // ],
      // onFilter: (value, record) => record.type.indexOf(value + '') === 0,
    },
    // {
    //   title: 'Group',
    //   dataIndex: 'group_name',
    //   sorter: (a, b) => (a.group || '').localeCompare(b.group || ''),
    //   render: (value) => value || '-',
    // },
    {
      title: t('data.Permission'),
      dataIndex: 'permission',
      sorter: (a, b) => a.permission.localeCompare(b.permission),
      render: (_value, row) => {
        return <VFolderPermissionTag permission={row.permission} />;
      },
    },
    {
      title: t('data.Created'),
      dataIndex: 'created_at',
      sorter: (a, b) => a.created_at.localeCompare(b.created_at),
      render: (value) => dayjs(value).format('L'),
      defaultSortOrder: 'descend',
    },
    // {
    //   title: 'Modified',
    //   dataIndex: 'modified',
    //   sorter: (a, b) => a.modified.localeCompare(b.modified),
    //   render: (value) => value || '-',
    // },
    // {
    //   title: 'Size',
    //   dataIndex: 'size',
    //   sorter: (a, b) => a.size - b.size,
    //   render: (value) => value || '-',
    // },
    // }
    // {
    //   title: 'Max Size',
    //   dataIndex: 'max_size',
    //   // sorter: (a, b) => a (a.max_size || '').localeCompare(b.max_size || ''),
    //   render: (value) => value || '-',
    // },
  ];
  return (
    <BAIFlex direction="column" align="stretch" gap={'xs'}>
      <BAIFlex direction="row" gap="xs" justify="between">
        <Input
          value={searchKey}
          onChange={(e) => setSearchKey(e.target.value)}
          allowClear
          placeholder={t('data.SearchByName')}
        />
        <Space.Compact>
          <Tooltip title={t('data.CreateANewStorageFolder')}>
            <BAIButton
              icon={<PlusIcon />}
              onClick={() => {
                setIsOpenCreateModal(true);
              }}
            />
          </Tooltip>
          <Tooltip title={t('button.Refresh')}>
            <Button
              loading={isPendingRefetch}
              icon={<ReloadOutlined />}
              onClick={() => {
                startRefetchTransition(() => {
                  updateFetchKey();
                });
              }}
            />
          </Tooltip>
        </Space.Compact>
      </BAIFlex>
      <Form form={internalForm} component={false}>
        <BAITable
          // size="small"
          scroll={{ x: 'max-content' }}
          rowKey={getRowKey}
          rowSelection={{
            selectedRowKeys,
            onChange: (selectedRowKeys) => {
              setSelectedRowKeys(selectedRowKeys as VFolderKey[]);
              handleAliasUpdate();
            },
          }}
          showSorterTooltip={false}
          columns={columns}
          dataSource={displayingFolders}
          onRow={(record) => {
            return {
              onClick: (event) => {
                const target = event.target as HTMLElement;
                // allow click on selection column
                if (target?.classList?.contains('ant-table-selection-column')) {
                  event.stopPropagation();
                  selectedRowKeys.includes(getRowKey(record))
                    ? setSelectedRowKeys(
                        selectedRowKeys.filter((k) => k !== getRowKey(record)),
                      )
                    : setSelectedRowKeys([
                        ...selectedRowKeys,
                        getRowKey(record),
                      ]);
                }
              },
            };
          }}
          {...tableProps}
        />
      </Form>
      {showAutoMountedFoldersSection && autoMountedFolderNames.length > 0 ? (
        <>
          <Descriptions size="small">
            <Descriptions.Item label={t('data.AutomountFolders')}>
              {_.map(autoMountedFolderNames, (name) => {
                return <Tag key={name}>{name}</Tag>;
              })}
            </Descriptions.Item>
          </Descriptions>
        </>
      ) : null}
      <FolderCreateModalV2
        open={isOpenCreateModal}
        onRequestClose={(result) => {
          setIsOpenCreateModal(false);
          if (result) {
            startRefetchTransition(() => {
              updateFetchKey();
              setSelectedRowKeys((x) => [
                ...x,
                // @ts-ignore
                result[rowKey],
              ]);
            });
          }
        }}
      />
    </BAIFlex>
  );
};

export default VFolderTable;
